import { useCallback, useEffect, useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import {
  buildGoogleMapsEmbedUrl,
  externalMapDirectionsUrl,
  externalMapSearchUrl,
  readBrowserMapsEmbedKey,
} from "@/lib/maps";
import { ServiceGateBanner } from "./ServiceGateBanner";
import { ghostBtnClass, inputClass, primaryBtnClass } from "./serviceShared";
import { useServiceAnchorLayerGate } from "./useServiceAnchorLayerGate";
import { useServiceBoardPanel } from "./useServiceBoardPanel";
import { useServiceMapPanel } from "./useServiceMapPanel";

export function ServiceMapPanel(props?: {
  embed?: boolean;
  refreshSignal?: number;
  /** When set (e.g. from Scheduling calendar pick), map filters to this date. */
  syncedRouteDate?: string;
}) {
  const embed = props?.embed ?? false;
  const gate = useServiceAnchorLayerGate();
  const board = useServiceBoardPanel(0);
  const [navTick, setNavTick] = useState(0);
  const effectiveTick = props?.refreshSignal ?? navTick;
  const m = useServiceMapPanel(effectiveTick, board.rigs, board.techs);
  const { setRouteDate } = m;

  useEffect(() => {
    if (props?.syncedRouteDate) setRouteDate(props.syncedRouteDate);
  }, [props?.syncedRouteDate, setRouteDate]);

  const handleRefresh = useCallback(() => {
    gate.refresh();
    void board.reload();
    setNavTick((s) => s + 1);
  }, [gate, board]);

  const embedUrl = useMemo(() => {
    const markers = m.suggestions
      .filter((s) => s.lat != null && s.lng != null)
      .map((s) => ({ lat: s.lat!, lng: s.lng! }));
    if (markers.length === 0) return null;
    return buildGoogleMapsEmbedUrl({
      markers,
      center: m.mapCenter ?? markers[0],
      apiKey: readBrowserMapsEmbedKey(),
      zoom: markers.length === 1 ? 13 : 10,
    });
  }, [m.suggestions, m.mapCenter]);

  const directionsUrl = useMemo(() => {
    const addresses = [...m.suggestions]
      .sort((a, b) => a.sequence - b.sequence)
      .map((s) => s.location_address.trim())
      .filter(Boolean);
    return externalMapDirectionsUrl(addresses);
  }, [m.suggestions]);

  const mapHeightClass = embed ? "h-80" : "h-72";

  const filtersRow = (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
      <input
        type="date"
        className={inputClass}
        value={m.routeDate}
        onChange={(e) => m.setRouteDate(e.target.value)}
      />
      <select
        className={inputClass}
        value={m.selectedRigId}
        onChange={(e) => m.setSelectedRigId(e.target.value)}
      >
        <option value="">All rigs (no rig filter)</option>
        {m.rigs.map((rig) => (
          <option key={rig.id} value={rig.id}>
            {rig.designation} ({rig.rig_kind})
          </option>
        ))}
      </select>
      <select
        className={inputClass}
        value={m.selectedTechId}
        onChange={(e) => m.setSelectedTechId(e.target.value)}
      >
        <option value="">Tech (optional)</option>
        {m.techs.map((tech) => (
          <option key={tech.id} value={tech.id}>
            {tech.display_name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={primaryBtnClass}
        disabled={m.applyBusy || !m.selectedRigId || m.suggestions.length === 0}
        onClick={() => void m.applyToRoute()}
      >
        {m.applyBusy ? "Applying…" : "Apply to route"}
      </button>
    </div>
  );

  const mapBody = gate.enabled ? (
    <>
      {filtersRow}

      {m.warnings.length > 0 ? (
        <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
          {m.warnings.map((w) => (
            <li key={w}>• {w}</li>
          ))}
        </ul>
      ) : null}

      <div className="rounded-2xl border border-gray-200 overflow-hidden dark:border-zinc-700">
        {embedUrl ? (
          <iframe
            title="Service route map"
            src={embedUrl}
            className={`w-full ${mapHeightClass} border-0 bg-gray-100 dark:bg-zinc-900`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <div className="px-4 py-8 text-center bg-gray-50 dark:bg-zinc-900/40">
            <p className="text-sm text-gray-600 dark:text-zinc-400">
              {m.listBusy ? "Loading map…" : "No geocoded stops for this date."}
            </p>
          </div>
        )}
        {directionsUrl ? (
          <div className="px-3 py-2 border-t border-gray-100 dark:border-zinc-800 text-right">
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-indigo-700 hover:underline dark:text-indigo-300"
            >
              Open full route in Google Maps ↗
            </a>
          </div>
        ) : null}
      </div>

      <SectionCard title="Suggested stops">
        {m.totalDriveHint ? (
          <p className="text-xs text-gray-500 dark:text-zinc-400 mb-2">{m.totalDriveHint}</p>
        ) : null}
        {m.listBusy && m.suggestions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">Loading suggestions…</p>
        ) : m.suggestions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-zinc-400">
            No upcoming events to map for this date.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-zinc-800">
            {[...m.suggestions]
              .sort((a, b) => a.sequence - b.sequence)
              .map((s) => (
                <li key={s.event_id} className="py-2 flex gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200">
                    {s.sequence}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                      {s.title}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-zinc-400">
                      {s.location_label || s.location_address || "No address"}
                      {s.scheduled_date ? ` · ${s.scheduled_date}` : ""}
                      {s.job_kind === "internal_upgrade" ? " · lot" : ""}
                    </div>
                    {s.hint ? (
                      <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">{s.hint}</p>
                    ) : null}
                  </div>
                  {s.location_address ? (
                    <a
                      href={externalMapSearchUrl(s.location_address)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-xs text-gray-500 hover:text-indigo-600 dark:text-zinc-400"
                    >
                      Map ↗
                    </a>
                  ) : null}
                </li>
              ))}
          </ul>
        )}
      </SectionCard>
    </>
  ) : null;

  const errorBanner =
    gate.enabled && m.localError ? (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
        {m.localError}
      </div>
    ) : null;

  const applyBanner =
    gate.enabled && m.applyMessage ? (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-100">
        {m.applyMessage}
      </div>
    ) : null;

  if (embed) {
    return (
      <div className="w-full text-left space-y-3">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">Route map</h3>
        {errorBanner}
        {applyBanner}
        {mapBody}
      </div>
    );
  }

  return (
    <div className="w-full text-left space-y-4">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h2 className="text-lg font-bold text-gray-900 dark:text-zinc-100">Service map</h2>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={handleRefresh}
          disabled={gate.loading || m.listBusy}
        >
          {m.listBusy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <p className="text-xs text-gray-600 dark:text-zinc-400">
        Suggestive route hints only — review stops and confirm on the Board before dispatch. Mixes
        internal upgrade (lot) and offering jobs when rig-compatible.
      </p>

      <ServiceGateBanner gate={gate} />
      {errorBanner}
      {applyBanner}
      {mapBody}
    </div>
  );
}
